import { useModal } from "@newsela/angelou";
import { objectTypes } from "@newsela/snitch";
import { NextRouter } from "next/router";
import { useState } from "react";

import AdvisoryModal from "@/assignments/AssignmentEditor/AdvisoryModal";
import {
  handleProvideConsent,
  handleSetAssignment,
} from "@/assignments/utils/assignmentActions";
import useSetAssignmentOnLoad from "@/assignments/utils/useSetAssignmentOnLoad";
import { BannerMode } from "@/types/BannerMode.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import { ISavingState } from "@/view/AssignActionsContainer/AssignActionsContainer.interface";
import UnlevelledContentActionsToolbar from "@/view/UnlevelledContentActionsToolbar/";
import UnlevelledContentAssignModal from "@/view/UnlevelledContent/UnlevelledContentAssignModal";
import useGetAssignmentHandlers from "@/view/UnlevelledContent/UnlevelledContentAssignModal/utils/useGetAssignmentHandlers";
import {
  ASSIGNMENT_MODE,
  currentAssignmentMode,
} from "@/view/utils/currentAssignmentMode";
import { AssignModalProps } from "@/view/utils/useAssignmentReducerContext";
import type { IUnlevelledContent } from "@/types/models/UnlevelledContent.interface";

interface AssignActionsProps {
  assignmentRequest: {
    data: Object;
    error: Error;
    loading: boolean;
  };
  assignModal: AssignModalProps;
  bannerMode: BannerMode;
  content: IUnlevelledContent;
  currentAssignment: Assignment;
  dispatch: () => void;
  fetchAssignment: Function;
  isSelectedAssignmentLoading: boolean;
  latestAssignment: Assignment;
  updatedAssignment: Assignment;
  userProperties: UserProperties;
  router: NextRouter;
}

const UnlevelledContentAssignActionsContainer = ({
  assignmentRequest,
  assignModal: {
    assignModalProps,
    assignModalTriggerRef,
    setIsAssignModalActive,
  },
  bannerMode,
  content,
  currentAssignment,
  dispatch,
  fetchAssignment,
  isSelectedAssignmentLoading,
  latestAssignment,
  updatedAssignment,
  userProperties,
  router,
}: AssignActionsProps) => {
  const [isOpeningAssignmentSettings, setIsOpeningAssignmentSettings] =
    useState(false);
  const [savingState, setSavingState] = useState<ISavingState>({
    type: null,
    value: false,
  });
  const [shareModalProps, setShareModalActive] = useModal(false);
  const [advisoryModalProps, setAdvisoryModalActive] = useModal(false);

  const { handlePublish, handleSaveDraft } = useGetAssignmentHandlers({
    content,
    dispatch,
    fetchAssignment,
    router,
    selectedAssignment: assignmentRequest.data as {
      assignment_id: number;
    },
    setIsAssignModalActive,
    setSavingState,
    setShareModalActive,
    setAdvisoryModalActive,
    updatedAssignment: updatedAssignment as Assignment,
    userProperties,
  });

  const assignmentMode = currentAssignmentMode(updatedAssignment);
  const isUpdateMode = assignmentMode === ASSIGNMENT_MODE.UPDATE;

  const handleAdvisoryConfirmation = () => {
    setAdvisoryModalActive(false);
    if (isUpdateMode) {
      isOpeningAssignmentSettings
        ? setIsAssignModalActive(true)
        : handlePublish();
    } else {
      setIsAssignModalActive(true);
    }
    setIsOpeningAssignmentSettings(false);
  };

  const updateContentSettingsOverride = (isSelected = false) => {
    handleProvideConsent(isSelected ? content.id : undefined, dispatch);
  };

  useSetAssignmentOnLoad({
    fetchAssignment,
    handleSetAssignment: (formattedAssignment: Assignment) =>
      handleSetAssignment(formattedAssignment, dispatch),
    isSelectedAssignmentLoading,
    isStudent: userProperties.isStudent,
    latestAssignment,
    selectedAssignment: currentAssignment,
    setIsLoading: undefined,
  });

  if (isSelectedAssignmentLoading) return null;

  return (
    <>
      <UnlevelledContentActionsToolbar
        assignModalTriggerRef={assignModalTriggerRef}
        bannerMode={bannerMode}
        content={content}
        currentAssignment={currentAssignment}
        dispatch={dispatch}
        handlePublish={handlePublish}
        isSaving={savingState}
        setAdvisoryModalActive={setAdvisoryModalActive}
        setIsAssignModalActive={setIsAssignModalActive}
        setIsOpeningAssignmentSettings={setIsOpeningAssignmentSettings}
        updatedAssignment={updatedAssignment as Assignment}
        userProperties={userProperties}
      />

      <UnlevelledContentAssignModal
        assignmentMode={assignmentMode}
        assignmentRequest={assignmentRequest}
        assignModalProps={assignModalProps}
        content={content}
        dispatch={dispatch}
        handlePublish={handlePublish}
        handleSaveDraft={handleSaveDraft}
        router={router}
        setIsAssignModalActive={setIsAssignModalActive}
        shareModalProps={shareModalProps}
        updatedAssignment={updatedAssignment}
        assignModalTriggerRef={assignModalTriggerRef}
      />

      {/* @ts-ignore Cannot be used as JSX */}
      <AdvisoryModal
        advisoryModalProps={advisoryModalProps}
        assignmentMembers={updatedAssignment?.assignment_members}
        contentMemberIds={[content.id]}
        handleAdvisoryConfirmation={() => handleAdvisoryConfirmation()}
        setAdvisoryModalActive={setAdvisoryModalActive}
        updateContentSettingsOverride={updateContentSettingsOverride}
        objectType={objectTypes.assignment}
      />
    </>
  );
};

export default UnlevelledContentAssignActionsContainer;
